import { SERVICE_LABEL } from "@/lib/utils/charlie/constants";
import { toDisplayLabel } from "@/lib/utils/daylight-insight/format";
import type { CharlieService } from "../types";

interface CharlieHeaderProps {
  service: CharlieService;
  setService: (s: CharlieService) => void;
  branch: string;
  setBranch: (b: string) => void;
  /** The user's real branch list, straight from the DayLight store. Values are
   *  raw branch names — `toDisplayLabel` is for display only, because the raw
   *  name is what the brief's rows are filtered against. */
  branches: string[];
  /** The brand mark is the way back to the rest of DayLight — the shared nav is
   *  not on screen while Charlie is. */
  onBrandClick: () => void;
}

/**
 * Charlie's own control row — brand, service line, branch.
 *
 * Charlie hides the DayLight brand row and owns the screen, so this is the only
 * header — which is why the logo navigates back to the Operating View. The
 * service toggle is still wired to the SHARED DayLight store rather than to
 * local state, so the service line the user picks here is the one waiting for
 * them on the dashboards.
 *
 * Role and the tracked-signal set are not on screen: both are administered in
 * the database and arrive with the config, so there is nothing here to pick.
 */
export function CharlieHeader({
  service,
  setService,
  branch,
  setBranch,
  branches,
  onBrandClick,
}: CharlieHeaderProps) {
  return (
    <header className="chead">
      <button className="cbrand" onClick={onBrandClick} title="Back to DayLight — Operating View">
        <svg width="30" height="30" viewBox="0 0 30 30">
          <circle cx="15" cy="15" r="11" fill="none" stroke="#EC6608" strokeWidth="2.6" strokeDasharray="1.6 3.1" strokeLinecap="round" />
          <circle cx="15" cy="15" r="5.4" fill="#EC6608" />
        </svg>
        <div>
          <div className="cname">Charlie</div>
          <div className="cby">by <b>Day<i>Light</i></b> · Skyra.AI</div>
        </div>
      </button>

      <div className="spacer" />

      <span className="svc">
        {(["hh", "hos"] as CharlieService[]).map((k) => (
          <button key={k} className={service === k ? "on" : ""} onClick={() => setService(k)}>
            {SERVICE_LABEL[k]}
          </button>
        ))}
      </span>

      <span className="ctl">
        Branch{" "}
        <select value={branch} onChange={(e) => setBranch(e.target.value)}>
          <option value="all">All my branches ({branches.length})</option>
          {branches.map((b) => (
            <option key={b} value={b}>{toDisplayLabel(b)}</option>
          ))}
        </select>
      </span>
    </header>
  );
}
